import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export interface ResponsiveTableCell {
  readonly id: string;
  readonly label: string;
  readonly content: ReactNode;
  readonly className?: string;
}

interface ResponsiveTableRowProps {
  readonly rowId: string;
  readonly cells: readonly ResponsiveTableCell[];
  readonly primaryCellIds: readonly string[];
  readonly className?: string;
}

const MEDIA_QUERY = '(pointer: coarse), (max-width: 1023px), (min-width: 1024px) and (max-width: 1279px) and (max-height: 1023px)';

/**
 * Mantém a tabela densa no desktop e revela os campos secundários por linha
 * em contextos de tablet. A condição acompanha a densidade `comfortable` do
 * shell sem alterar o shell global.
 */
export function useTabelaEmContextoTablet(): boolean {
  const [emContextoTablet, setEmContextoTablet] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(MEDIA_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(MEDIA_QUERY);
    const atualizar = () => setEmContextoTablet(media.matches);
    atualizar();
    media.addEventListener('change', atualizar);
    return () => media.removeEventListener('change', atualizar);
  }, []);

  return emContextoTablet;
}

/**
 * Produz apenas linhas válidas dentro de `tbody`: a linha principal e, quando
 * solicitada, uma segunda linha com os detalhes já carregados para o registro.
 */
export function ResponsiveTableRow({ rowId, cells, primaryCellIds, className }: ResponsiveTableRowProps) {
  const emContextoTablet = useTabelaEmContextoTablet();
  const [expandida, setExpandida] = useState(false);
  const detalheId = `detalhes-${rowId}`;
  const primaryCells = cells.filter((celula) => primaryCellIds.includes(celula.id));
  const secondaryCells = cells.filter((celula) => !primaryCellIds.includes(celula.id));
  const podeExpandir = emContextoTablet && secondaryCells.length > 0;

  return (
    <Fragment>
      <tr className={className} data-testid={`linha-responsiva-${rowId}`}>
        {(emContextoTablet ? primaryCells : cells).map((celula) => (
          <td key={celula.id} className={`p-3 align-top ${celula.className ?? ''}`}>
            {celula.content}
          </td>
        ))}
        {podeExpandir && (
          <td className="p-2 text-right align-top">
            <button
              type="button"
              aria-expanded={expandida}
              aria-controls={detalheId}
              aria-label={`${expandida ? 'Ocultar' : 'Mostrar'} detalhes da linha`}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded border border-input text-foreground outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
              onClick={() => setExpandida((aberta) => !aberta)}
            >
              <ChevronDown className={`size-4 transition-transform ${expandida ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
          </td>
        )}
      </tr>
      {podeExpandir && expandida && (
        <tr id={detalheId} data-testid={`detalhes-responsivos-${rowId}`} className="bg-muted/30">
          <td colSpan={primaryCells.length + 1} className="p-3">
            <dl className="grid gap-3 sm:grid-cols-2">
              {secondaryCells.map((celula) => (
                <div key={celula.id} className="min-w-0">
                  <dt className="text-xs font-medium text-muted-foreground">{celula.label}</dt>
                  <dd className="mt-1 break-words text-xs text-foreground">{celula.content}</dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </Fragment>
  );
}
